import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useWorks } from '../contexts/WorksContext';
import { Work, WorkCategory } from '../models/Work';
import { createSlug } from './WorkDetail';

const WorksContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

// Use sticky positioning for the header
const FiltersHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: #fff;
  padding: 20px 10px 15px 10px;
  border-bottom: 1px solid #f5f5f5;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const PageTitle = styled.h1`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.65rem;
  margin: 0 0 5px 0;
  color: #333;
  padding: 0 10px;
`;

// New Search Input component
const SearchInput = styled.input`
  width: calc(100% - 20px);
  padding: 10px 12px;
  border: 1px solid #ddd;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  border-radius: 4px;
  box-sizing: border-box;
  margin: 0 10px;

  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  }
`;

const FilterSection = styled.div`
  background-color: #fff;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding: 0 10px;
`;

const FilterGroup = styled.div`
  flex: 1;
  min-width: 150px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.675rem;
  font-weight: 500;
  color: #555;
`;

const CategoryButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CategoryButton = styled.button<{ isActive: boolean }>`
  background-color: ${props => props.isActive ? '#333' : '#fff'};
  color: ${props => props.isActive ? '#fff' : '#333'};
  border: 1px solid ${props => props.isActive ? '#333' : '#ddd'};
  border-radius: 3px;
  padding: 6px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 0.7125rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.isActive ? '#333' : '#999'};
  }
`;

// Scrollable content area
const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-left: 10px;
  padding-right: 25px;
  padding-bottom: 20px;
  padding-top: 10px;
`;

const WorksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  /* Ensure list takes up space even if empty initially */
  min-height: 1px; 
`;

const WorkItem = styled.div`
  background-color: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1.5px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    box-shadow: 0 3.75px 11.25px rgba(0, 0, 0, 0.1);
    transform: translateY(-1.5px);
  }
`;

const WorkItemContent = styled.div`
  padding: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const WorkTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: nowrap; /* Prevent wrapping of Tag/Summary relative to Title */
  flex-grow: 1;
  /* Ensure this section can shrink if needed, but prioritize title */
  min-width: 0; /* Important for flex item text overflow */
`;

const WorkTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  color: #333;
  white-space: nowrap; /* Keep title on one line */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ... for long titles */
  min-width: 0; /* Allow shrinking */
  flex-shrink: 1; /* Allow title to shrink before Tag/Summary */
`;

// New Component for Category Tag
const CategoryTag = styled.span<{ category: WorkCategory }>`
  font-size: 0.675rem;
  color: #333;
  padding: 3px 10px;
  border-radius: 1em;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: ${props => {
    switch (props.category) {
      case WorkCategory.CHORAL: return '#AEC6CF'; // Pastel Blue/Grey
      case WorkCategory.VOCAL: return '#B9E4A5'; // Pastel Green
      case WorkCategory.INSTRUMENTAL: return '#FFB3BA'; // Pastel Red
      default: return '#E0E0E0'; // Light Grey fallback
    }
  }};
`;

const WorkSummary = styled.div`
  display: flex;
  gap: 15px;
  font-family: 'Inter', sans-serif;
  align-items: center;
  /* margin-left: 15px; <-- Remove this, rely on gap */
  flex-shrink: 0; /* Prevent summary from shrinking */
`;

const WorkMeta = styled.div`
  font-size: 0.75rem; /* Increased font size */
  color: #666;
  display: flex;
  align-items: center;
`;

// Specific component for duration with clock
const DurationMeta = styled(WorkMeta)`
  &:before {
    content: '⏱︎';
    display: inline-block;
    background-color: transparent;
    border-radius: 0;
    margin-right: 4px;
    font-size: 0.9em;
    line-height: 1;
  }
`;

const ArrowIcon = styled.div`
  width: 18px;
  height: 18px;
  position: relative;
  
  &:after {
    content: '›';
    font-size: 18px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #555;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 0;
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 0.825rem;
`;

const NoWorksMessage = styled.div`
  text-align: center;
  padding: 45px 0;
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
`;

const Works: React.FC = () => {
  const { works, loading } = useWorks();
  const navigate = useNavigate();
  const [filteredWorks, setFilteredWorks] = useState<Work[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [ensembleTypeFilter, setEnsembleTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Get unique ensemble types based on the selected category
  const availableEnsembleTypes = useMemo(() => {
    const uniqueTypes = new Set<string>();

    works
      .filter(work => categoryFilter === 'all' || work.category === categoryFilter)
      .forEach(work => {
        if (work.ensembleType) {
          uniqueTypes.add(work.ensembleType);
        }
      });

    return Array.from(uniqueTypes).sort();
  }, [works, categoryFilter]);

  useEffect(() => {
    let filtered = [...works];

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(work => work.category === categoryFilter);
    }

    if (ensembleTypeFilter !== 'all') {
      filtered = filtered.filter(work => work.ensembleType === ensembleTypeFilter);
    }

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(work =>
        work.title.toLowerCase().includes(lowerSearchTerm)
      );
    }

    filtered.sort((a, b) => b.year - a.year);

    setFilteredWorks(filtered);
  }, [works, categoryFilter, ensembleTypeFilter, searchTerm]);

  const handleCategoryChange = (category: string) => {
    setCategoryFilter(category);
    setEnsembleTypeFilter('all');
  };

  const handleEnsembleTypeChange = (type: string) => {
    setEnsembleTypeFilter(type);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleWorkClick = (work: Work) => {
    const slug = createSlug(work.title);
    navigate(`/works/${slug}`);
  };

  return (
    <WorksContainer>
      <FiltersHeader>
        <PageTitle>Works</PageTitle>
        <SearchInput
          type="search"
          placeholder="Search by title..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <FilterSection>
          <FilterGroup>
            <FilterLabel htmlFor="category-filter">Category</FilterLabel>
            <CategoryButtonsContainer id="category-filter">
              <CategoryButton
                onClick={() => handleCategoryChange('all')}
                isActive={categoryFilter === 'all'}
              >
                All
              </CategoryButton>
              {Object.values(WorkCategory).map(cat => (
                <CategoryButton
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  isActive={categoryFilter === cat}
                >
                  {cat}
                </CategoryButton>
              ))}
            </CategoryButtonsContainer>
          </FilterGroup>

          {categoryFilter !== 'all' && availableEnsembleTypes.length > 0 && (
            <FilterGroup>
              <FilterLabel htmlFor="ensemble-type-filter">Ensemble Type</FilterLabel>
              <CategoryButtonsContainer id="ensemble-type-filter">
                <CategoryButton
                  onClick={() => handleEnsembleTypeChange('all')}
                  isActive={ensembleTypeFilter === 'all'}
                >
                  All
                </CategoryButton>
                {availableEnsembleTypes.map(type => (
                  <CategoryButton
                    key={type}
                    onClick={() => handleEnsembleTypeChange(type)}
                    isActive={ensembleTypeFilter === type}
                  >
                    {type}
                  </CategoryButton>
                ))}
              </CategoryButtonsContainer>
            </FilterGroup>
          )}
        </FilterSection>
      </FiltersHeader>

      <ScrollableContent>
        <WorksList>
          {loading ? (
            <LoadingMessage>Loading works...</LoadingMessage>
          ) : filteredWorks.length === 0 ? (
            <NoWorksMessage>No works found matching the selected filters.</NoWorksMessage>
          ) : (
            filteredWorks.map(work => (
              <WorkItem key={work.title} onClick={() => handleWorkClick(work)}>
                <WorkItemContent>
                  <WorkTitleSection>
                    <WorkTitle>{work.title}</WorkTitle>
                    <CategoryTag category={work.category}>{work.category}</CategoryTag>
                    <WorkSummary>
                      {work.duration && <DurationMeta>{work.duration}</DurationMeta>}
                      <WorkMeta>{work.year}</WorkMeta>
                    </WorkSummary>
                  </WorkTitleSection>
                  <ArrowIcon />
                </WorkItemContent>
              </WorkItem>
            ))
          )}
        </WorksList>
      </ScrollableContent>
    </WorksContainer>
  );
};

export default Works; 